/**
 * @file schemas/crops.ts
 * @description 可驗證作物字典 Zod schemas（管理頁 CRUD 與下拉共用）。
 *
 * 主要類別（CROP_MAIN_CATEGORIES）固定由法規定義，
 * 次要類別、品項組與品項名稱由資料庫維護。
 */
import { z } from 'zod';
/** 驗證方案：TOP（有機）/ TAP（產銷履歷），兩者主要類別枚舉不同（序157） */
export const CROP_SCHEMES = ['TOP', 'TAP'];
/** TAP（產銷履歷）法規定義的四個主要類別（固定，不允許自定義） */
export const CROP_MAIN_CATEGORIES_TAP = ['農糧產品', '畜禽產品', '養殖水產品', '林產品'];
/** TOP（有機）主要類別（固定，不允許自定義） */
export const CROP_MAIN_CATEGORIES_TOP = ['有機農糧產品', '有機加工品'];
/** 舊名保留：泛指所有 scheme 的主要類別聯集，供既有引用相容 */
export const CROP_MAIN_CATEGORIES = [
    ...CROP_MAIN_CATEGORIES_TAP,
    ...CROP_MAIN_CATEGORIES_TOP,
];
/** 依 scheme 取得對應的主要類別枚舉，供表單下拉與校驗共用 */
export function mainCategoriesForScheme(scheme) {
    return scheme === 'TOP' ? CROP_MAIN_CATEGORIES_TOP : CROP_MAIN_CATEGORIES_TAP;
}
const baseShape = {
    subCategory: z.string().min(1).max(100),
    itemGroup: z.string().max(100).optional().nullable(),
    name: z.string().min(1).max(200),
    /** 產品範圍（僅 TOP 使用；TAP 留空） */
    productScope: z.string().max(200).optional().nullable(),
    remark: z.string().max(500).optional().nullable(),
    isActive: z.boolean().default(true),
    sortOrder: z.number().int().min(0).default(0),
};
/** 新增作物（POST 校驗）；scheme + mainCategory 必填，mainCategory 需屬該 scheme 的法規枚舉 */
export const createCropSchema = z
    .object({
    scheme: z.enum(CROP_SCHEMES).default('TAP'),
    mainCategory: z.enum(CROP_MAIN_CATEGORIES),
    ...baseShape,
})
    .refine((v) => mainCategoriesForScheme(v.scheme).includes(v.mainCategory), {
    message: 'mainCategory 與 scheme 不匹配',
    path: ['mainCategory'],
});
/** 局部更新（PATCH 校驗）；任何欄位皆可省略，不做跨欄位 refine（避免只改單一欄位時誤判） */
export const updateCropSchema = z
    .object({
    scheme: z.enum(CROP_SCHEMES).optional(),
    mainCategory: z.enum(CROP_MAIN_CATEGORIES).optional(),
    ...baseShape,
})
    .partial();
/**
 * 列表 query 參數。
 *  - activeOnly=true 僅回啟用且未軟刪（給下拉用）
 *  - includeDeleted=true 回傳含已軟刪（admin 視圖；預設 false）
 *  - scheme 按 TOP/TAP 方案過濾（序230）
 *  - mainCategory 按主要類別過濾（前端 Tab 切換）
 *  - subCategory 按次要類別過濾
 */
export const cropQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(500).default(50),
    search: z.string().optional(),
    scheme: z.enum(CROP_SCHEMES).optional(),
    mainCategory: z.enum(CROP_MAIN_CATEGORIES).optional(),
    subCategory: z.string().optional(),
    activeOnly: z.coerce.boolean().optional(),
    includeDeleted: z.coerce.boolean().optional(),
});
/** 出參 DTO */
export const cropDtoSchema = z.object({
    id: z.number().int().positive(),
    scheme: z.enum(CROP_SCHEMES),
    mainCategory: z.string(),
    subCategory: z.string(),
    itemGroup: z.string().nullable(),
    name: z.string(),
    productScope: z.string().nullable(),
    remark: z.string().nullable(),
    isActive: z.boolean(),
    sortOrder: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
    deletedAt: z.string().nullable(),
});
/**
 * 派案「補建有機驗證範圍」請求（POST /api/settings/crops/ensure-top-scopes）。
 *
 * 2026-09-07 回報序7：新建/修改案件的有機申請明細改自由文字輸入驗證範圍後，系統比對
 * 作物管理中該【驗證品項】（TOP subCategory）底下尚未建立的產品範圍，經派案人員確認
 * 「是」後自動補建到作物管理。能力沿用 case.dispatch（派案人員），不需 system-settings.write。
 */
export const ensureTopCropScopesSchema = z.object({
    entries: z
        .array(z.object({
        /** 有機主要類別（有機農糧產品 / 有機加工品） */
        mainCategory: z.enum(CROP_MAIN_CATEGORIES_TOP),
        /** 驗證品項（字典 subCategory，如「大漿果」） */
        subCategory: z.string().trim().min(1).max(100),
        /** 要補建的驗證範圍名稱（字典 name，如「香蕉」） */
        name: z.string().trim().min(1).max(200),
    }))
        .min(1)
        .max(200),
});
